import { Component, Input, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormArray, FormControl,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
} from '@angular/forms';

import {OutlineButtonComponent} from "../../../../shared/ui/outline-button/outline-button.component";
import {faAdd, faChevronDown, faXmark} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {DropdownMenuComponent, MenuItem} from "../../../../shared/ui/dropdown-menu/dropdown-menu.component";
import { DropdownSearchComponent } from 'src/app/shared/ui/dropdown-search/dropdown-search.component';

interface Assessment {
  title: string
  instructions: string
  expanded: boolean
}
@Component({
  selector: 'app-exam-link',
  templateUrl: './link.component.html',
  imports: [ReactiveFormsModule, OutlineButtonComponent, FontAwesomeModule, DropdownSearchComponent, DropdownMenuComponent]
})
export class LinkComponent implements OnInit {
  @Input() formGroupName!: string;
  linkFormSection!: FormGroup;
  rootFormGroup = inject(FormGroupDirective);
  faAdd = faAdd
  faChevronDown = faChevronDown
  selectedFirstPart?: MenuItem
  selectedConditionalPart?: MenuItem

  ngOnInit() {
    this.linkFormSection = this.rootFormGroup.control as FormGroup;

    this.flows.controls.forEach((flowForm) => this.setupFlowListener(flowForm as FormGroup));
  }

  get flows(): FormArray {
    return this.linkFormSection.get('flows') as FormArray;
  }

  get parts(): FormArray {
    return this.linkFormSection.get('construct')?.get('general')?.get('parts') as FormArray
  }

  flowGroup(i: number): FormGroup {
    return this.flows.at(i) as FormGroup
  }

  addConditional() {
    const flowForm = new FormGroup({
      examflow_id: new FormControl(null),
      exam_id: new FormControl(null),
      exampart_previous: new FormControl(null),
      exampart_next: new FormControl(null),
      eval_sequence: new FormControl(this.flows.length),
      req_overallscore_min: new FormControl(null),
      req_overallscore_max: new FormControl(null),
      req_partscore_min: new FormControl(null),
      req_partscore_max: new FormControl(null),
      req_qpool_id: new FormControl(null),
      req_qpool_correctness_min: new FormControl(null),
      req_qpool_correctness_max: new FormControl(null),
      req_q_id: new FormControl(null),
      req_q_correctness: new FormControl(null),
      req_q_correctness_range: new FormControl(null),
      req_q_response_value: new FormControl(null),
      req_q_response_formatted: new FormControl(null),
      flow_noMin: new FormControl(false),
      flow_noMax: new FormControl(false),
      _original_unconditional_next_part: new FormControl(null),
    })
    this.flows.push(flowForm)
    this.setupFlowListener(flowForm)
  }

  removeConditional(i: number) {
    const flowForm = this.flowGroup(i);
    const previousValue = flowForm.get('exampart_previous')?.value;
    if (previousValue !== null && previousValue !== undefined) {
      this.restorePartUnconditionalNext(previousValue, flowForm);
    }
    this.flows.removeAt(i);
  }

  noMinClick(flow: AbstractControl) {
    if(flow.get('flow_noMin')?.value as boolean) {
      flow.get('req_partscore_min')?.setValue(null)
    }
    flow.get('flow_noMin')?.setValue(!flow.get('flow_noMin')?.value as boolean)
  }

  noMaxClick(flow: AbstractControl) {
    if(flow.get('flow_noMax')?.value as boolean) {
      flow.get('req_partscore_max')?.setValue(null)
    }
    flow.get('flow_noMax')?.setValue(!flow.get('flow_noMax')?.value as boolean)
  }

  private setupFlowListener(flowForm: FormGroup) {
    let previousValue = flowForm.get('exampart_previous')?.value ?? null;

    // Edit mode: if this flow already has a part selected on load, null it now.
    if (previousValue !== null) {
      this.nullifyPartUnconditionalNext(previousValue, flowForm);
    }

    flowForm.get('exampart_previous')?.valueChanges.subscribe((newValue) => {
      if (previousValue !== null && previousValue !== undefined) {
        this.restorePartUnconditionalNext(previousValue, flowForm);
      }
      if (newValue !== null && newValue !== undefined) {
        this.nullifyPartUnconditionalNext(newValue, flowForm);
      }
      previousValue = newValue;
    });
  }

  private findPart(exampart_value: string | number): FormGroup | undefined {
    const target = Number(exampart_value);
    return this.parts.controls.find((part, index) => {
      const examPartId = part.get('exampart_id')?.value;
      if (examPartId != null) {
        return Number(examPartId) === target;
      }
      return index * -1 === target;
    }) as FormGroup | undefined;
  }

  private nullifyPartUnconditionalNext(exampart_value: string | number, flowForm: FormGroup) {
    const part = this.findPart(exampart_value);
    if (part) {
      flowForm.get('_original_unconditional_next_part')?.setValue(part.get('unconditional_next_part')?.value);
      part.get('unconditional_next_part')?.setValue(null);
    }
  }

  private restorePartUnconditionalNext(exampart_value: string | number, flowForm: FormGroup) {
    const part = this.findPart(exampart_value);
    if (part) {
      const original = flowForm.get('_original_unconditional_next_part')?.value;
      part.get('unconditional_next_part')?.setValue(original);
      flowForm.get('_original_unconditional_next_part')?.setValue(null);
    }
  }

  // TODO need to get the assessments and display the options to the user via what is in that list
  protected readonly faXmark = faXmark;
  protected readonly FormControl = FormControl;
}
